import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Pencil, User, Mail, Key, AlertCircle, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../services/authentication/authContext';
import { signOut as supabaseSignOut } from '../../../services/authentication/supabaseService';
import { fetchUserProfile, deleteUser as deleteUserService, updateUserProfile, initiateEmailChange } from '../../../services/user/userService';
import type { BackendUser } from '../../../services/user/userService';
import { Header } from '../landingpage/sections/Header';
import { useSubscription } from '../../../hooks/useSubscription';
import DemoProfile from './DemoProfile';

const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user: authUser, backendUser, setUser } = useAuth();
  const { isActive, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<BackendUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');
  const [emailChangeStatus, setEmailChangeStatus] = useState<'idle' | 'pending' | 'error'>('idle');

  // fetch user profile
  useEffect(() => {
    if (!authUser) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const data = await fetchUserProfile();
        setProfile(data);
      } catch (err: any) {
        console.error('Error fetching profile', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [authUser, navigate]);

  if (!authUser || loading || !profile) {
    return (
      <div className="relative overflow-hidden min-h-screen bg-landingPageBG dark:bg-landingPageBG-dark text-white dark:text-white">
        <Header />
        <div className="flex justify-center px-5 py-20" role={error ? 'alert' : 'status'} aria-busy={loading}>
          {error ? <p className="text-red-200 dark:text-red-200">{error}</p> : <>
            <Loader2 className="h-6 w-6 animate-spin text-white/70 dark:text-white/70" aria-hidden="true" />
            <span className="sr-only">{t('common.loading')}</span>
          </>}
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error', err);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUserService();
      // The account is gone server-side, but the Supabase session still sits in
      // localStorage — without a local sign-out getSession() restores the deleted
      // user on the next load and routes them into the app with nothing to show.
      await supabaseSignOut().catch(err => console.error('Sign out after account deletion failed', err));
      setUser(null);
      navigate('/login', { replace: true });
    } catch (err: any) {
      console.error('Error deleting account', err);
      setError(err.message);
      setShowDeleteModal(false);
    }
  };

  const handleNameEdit = () => {
    setEditedName(profile?.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (!editedName.trim()) return;

    setIsUpdatingProfile(true);
    try {
      const updatedUser = await updateUserProfile(editedName);
      setProfile(updatedUser);
      setIsEditingName(false);
      setError(null);
    } catch (err: any) {
      console.error('Error updating name', err);
      setError(err.message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleEmailEdit = () => {
    setEditedEmail(profile?.email || '');
    setIsEditingEmail(true);
    setEmailChangeStatus('idle');
  };

  const handleEmailSave = async () => {
    if (!editedEmail.trim()) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsUpdatingProfile(true);
    setEmailChangeStatus('idle');
    try {
      await initiateEmailChange(editedEmail);
      setEmailChangeStatus('pending');
      setIsEditingEmail(false);
      setError(null);
    } catch (err: any) {
      console.error('Error initiating email change', err);
      setError(err.message);
      setEmailChangeStatus('error');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleEmailCancel = () => {
    setIsEditingEmail(false);
    setEditedEmail('');
    setEmailChangeStatus('idle');
  };

  if (backendUser?.demoContext) return <DemoProfile profile={profile} onLogout={handleLogout} />;

  if (!subscriptionLoading && isActive) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG dark:bg-landingPageBG-dark text-white dark:text-white">
      <Header />
      <div className="flex flex-col justify-center items-center min-h-screen px-5 md:px-8 py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white dark:text-white mb-4">
            Your Account
          </h1>
          <p className="text-lg text-white/70 dark:text-white/70 max-w-2xl mx-auto">
            Manage your personal information and account settings
          </p>
        </div>

        {/* Profile Card */}
        <div className="w-full max-w-2xl mx-auto">
          <div className="rounded-2xl border border-white/10 dark:border-white/10 p-8 bg-white/5 dark:bg-white/5 backdrop-blur-sm">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 dark:bg-red-500/10 rounded-xl border border-red-500/30 dark:border-red-500/30 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-200 dark:text-red-200">{error}</p>
              </div>
            )}

            {emailChangeStatus === 'pending' && (
              <div className="mb-6 p-4 bg-amber-500/10 dark:bg-amber-500/10 rounded-xl border border-amber-500/30 dark:border-amber-500/30 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200 dark:text-amber-200">
                  <p className="font-semibold mb-1">Email change pending</p>
                  <p>Please check both your old and new email for confirmation links.</p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Name field with edit */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-white/50 dark:text-white/50" />
                  <h3 className="text-sm font-semibold text-white/70 dark:text-white/70">Name</h3>
                </div>
                {isEditingName ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="flex-1 px-4 py-3 rounded-xl border border-white/20 dark:border-white/20 bg-white/5 dark:bg-white/5 text-white dark:text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-accent/50 dark:focus:ring-accent/50 focus:border-white/30 transition"
                      placeholder="Enter your name"
                    />
                    <button
                      onClick={handleNameSave}
                      disabled={isUpdatingProfile || !editedName.trim()}
                      className="px-4 py-3 bg-accent dark:bg-accent text-white dark:text-white rounded-xl hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleNameCancel}
                      disabled={isUpdatingProfile}
                      className="px-4 py-3 border border-white/20 dark:border-white/20 text-white/80 dark:text-white/80 rounded-xl hover:bg-white/5 dark:hover:bg-white/5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10">
                    <p className="text-white dark:text-white">{profile.name || 'Not set'}</p>
                    <button
                      onClick={handleNameEdit}
                      className="p-2 hover:bg-white/10 dark:hover:bg-white/10 rounded-lg transition group"
                    >
                      <Pencil className="w-4 h-4 text-white/50 dark:text-white/50 group-hover:text-white/80 dark:group-hover:text-white/80" />
                    </button>
                  </div>
                )}
              </div>

              {/* Email field with edit (admin only) */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-white/50 dark:text-white/50" />
                  <h3 className="text-sm font-semibold text-white/70 dark:text-white/70">Email Address</h3>
                </div>
                {isEditingEmail ? (
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={editedEmail}
                      onChange={(e) => setEditedEmail(e.target.value)}
                      className="flex-1 px-4 py-3 rounded-xl border border-white/20 dark:border-white/20 bg-white/5 dark:bg-white/5 text-white dark:text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-accent/50 dark:focus:ring-accent/50 focus:border-white/30 transition"
                      placeholder="Enter your email"
                    />
                    <button
                      onClick={handleEmailSave}
                      disabled={isUpdatingProfile || !editedEmail.trim()}
                      className="px-4 py-3 bg-accent dark:bg-accent text-white dark:text-white rounded-xl hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleEmailCancel}
                      disabled={isUpdatingProfile}
                      className="px-4 py-3 border border-white/20 dark:border-white/20 text-white/80 dark:text-white/80 rounded-xl hover:bg-white/5 dark:hover:bg-white/5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10">
                    <p className="text-white dark:text-white">{profile.email}</p>
                    {backendUser?.role === 'admin' && (
                      <button
                        onClick={handleEmailEdit}
                        disabled={emailChangeStatus === 'pending'}
                        className="p-2 hover:bg-white/10 dark:hover:bg-white/10 rounded-lg transition group disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Pencil className="w-4 h-4 text-white/50 dark:text-white/50 group-hover:text-white/80 dark:group-hover:text-white/80" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* User ID (read-only) */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4 text-white/50 dark:text-white/50" />
                  <h3 className="text-sm font-semibold text-white/70 dark:text-white/70">User ID</h3>
                </div>
                <div className="p-4 rounded-xl bg-white/5 dark:bg-white/5 border border-white/10 dark:border-white/10">
                  <p className="text-white/80 dark:text-white/80 font-mono text-sm">{profile.id}</p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-8 pt-6 border-t border-white/10 dark:border-white/10 space-y-3">
              <button
                onClick={handleLogout}
                className="w-full px-6 py-4 bg-white/10 dark:bg-white/10 hover:bg-white/15 text-white dark:text-white rounded-2xl transition font-medium border border-white/10 dark:border-white/10"
              >
                Logout
              </button>
              {backendUser?.role === 'admin' && (
                <button
                  onClick={handleDeleteClick}
                  className="w-full px-6 py-4 bg-red-500/10 dark:bg-red-500/10 hover:bg-red-500/20 text-red-200 dark:text-red-200 rounded-2xl transition font-medium border border-red-500/30 dark:border-red-500/30"
                >
                  Delete Account
                </button>
              )}
            </div>
          </div>

          {/* Trust Info */}
          <div className="mt-8 pt-6 border-t border-white/10 dark:border-white/10">
            <div className="flex flex-wrap justify-center items-center gap-6 text-white/50 dark:text-white/50 text-sm">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span>Secure & Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                <span>GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 dark:bg-black/70 backdrop-blur-sm"
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="bg-landingPageBG dark:bg-landingPageBG-dark rounded-3xl shadow-2xl w-full max-w-md border border-white/20 dark:border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-white dark:text-white">
                  Delete Account?
                </h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-none p-2 rounded-full hover:bg-white/10 dark:hover:bg-white/10 transition"
                  aria-label="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                  </svg>
                </button>
              </div>

              <div className="mb-6 p-4 rounded-xl bg-red-500/10 dark:bg-red-500/10 border border-red-500/30 dark:border-red-500/30">
                <div className="flex gap-3">
                  <div className="flex-none mt-0.5">
                    <div className="h-8 w-8 rounded-lg bg-red-500/20 dark:bg-red-500/20 text-red-400 dark:text-red-400 grid place-items-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-red-200 dark:text-red-200 font-medium mb-1">This action cannot be undone</p>
                    <p className="text-xs text-red-200/80 dark:text-red-200/80 leading-relaxed">
                      Deleting your account will permanently remove all your data, including restaurants, orders, and reports. This cannot be reversed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-white/20 dark:border-white/20 text-white dark:text-white hover:bg-white/5 dark:hover:bg-white/5 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-red-600 dark:bg-red-600 text-white dark:text-white hover:bg-red-700 transition"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile;
